import {
    FormDto,
    FormSectionDto,
    FormRowDto,
    FieldDto,
} from 'app/models/generated'
import { logger } from 'app/logging/Logger'

// Track if we've already attempted a resync to avoid infinite loops
let hasAttemptedResync = false

// Note: Removed resyncPromise tracking - using simple page reload instead

export type AccForm = {
    id: string
    type: string
    consultId: string
    title: string
    status: string
    sentDate: Date
    sections: AccFormSection[]
}

export type AccFormSection = {
    title: string
    rows: AccFormRow[]
}

export type AccFormColumnGroup = AccField[]

export type AccFormRow = {
    columns: AccFormColumnGroup[]
}

type AccField = {
    label: string
    content: string
    linkId?: string
    code?: string
    system?: string
    codings?: Array<{
        system?: string
        code?: string
        display?: string
    }>
}

/**
 * Convert FormDto from DataStore to AccForm format expected by FormReview component.
 *
 * Note: The FormRowDto type definition is incorrect - it shows columns as a flat array,
 * but the actual DynamoDB data has nested arrays (array of column groups).
 * This converter handles the actual nested structure.
 *
 * If null/invalid data is detected, triggers a DataStore clear and page reload.
 */
export const convertFormDtoToAccForm = (formDto: FormDto): AccForm => {
    // Debug: Log the complete structure to understand what DataStore is returning
    const firstSection = formDto.sections?.[0]
    const firstRow = firstSection?.rows?.[0]
    const columns = firstRow?.columns
    const firstColumn = columns?.[0]

    // Check if we have sections but rows are null/invalid
    const hasInvalidData =
        formDto.sections &&
        formDto.sections.length > 0 &&
        formDto.sections.some(
            (section) =>
                section &&
                section.rows !== null &&
                section.rows !== undefined &&
                !Array.isArray(section.rows)
        )

    if (hasInvalidData && !hasAttemptedResync) {
        logger.warn(
            'Detected invalid form data structure, clearing DataStore and reloading',
            {
                action: 'invalid_form_data_detected',
                metadata: {
                    formId: formDto.id,
                    formType: formDto.type,
                    hasSection: !!firstSection,
                    sectionTitle: firstSection?.title,
                    rowsType: typeof firstSection?.rows,
                    rowsIsArray: Array.isArray(firstSection?.rows),
                },
            }
        )

        // Mark that we've attempted resync to avoid infinite loops
        hasAttemptedResync = true

        // Clear DataStore and reload page
        triggerDataStoreResync(formDto.id)
    }

    logger.debug('FormDto from DataStore - detailed structure:', {
        action: 'convert_form_dto',
        metadata: {
            hasSection: !!firstSection,
            sectionTitle: firstSection?.title,
            hasRows: !!firstSection?.rows,
            rowCount: firstSection?.rows?.length || 0,
            firstRow: firstRow,
            hasColumns: !!columns,
            columnsType: Array.isArray(columns) ? 'array' : typeof columns,
            columnsLength: Array.isArray(columns) ? columns.length : 'N/A',
            firstColumn: firstColumn,
            firstColumnType: Array.isArray(firstColumn)
                ? 'array'
                : typeof firstColumn,
            firstColumnKeys: firstColumn ? Object.keys(firstColumn) : [],
            // Try to access the first field if columns is nested
            possibleField: Array.isArray(firstColumn)
                ? firstColumn[0]
                : firstColumn,
        },
    })

    return {
        id: formDto.id,
        type: formDto.type,
        consultId: formDto.consultId,
        title: formDto.title,
        status: formDto.status,
        sentDate: new Date(formDto.sentDate),
        sections: convertSections(formDto.sections),
    }
}

/**
 * Trigger page reload to recover from corrupted form data.
 * Previously cleared DataStore cache, now just reloads the page since
 * form data is fetched via direct GraphQL queries.
 */
function triggerDataStoreResync(formId: string): void {
    logger.info('Reloading page to recover from corrupted form data', {
        action: 'page_reload_recovery',
        metadata: { formId },
    })
    window.location.reload()
}

const convertSections = (
    sections?: (FormSectionDto | null)[] | null
): AccFormSection[] => {
    if (!sections) return []

    return sections
        .filter((section): section is FormSectionDto => section !== null)
        .map((section) => ({
            title: section.title || '',
            rows: convertRows(section.rows, section.title || undefined),
        }))
}

const convertRows = (
    rows?: (FormRowDto | null)[] | null,
    sectionTitle?: string
): AccFormRow[] => {
    logger.debug('convertRows called:', {
        action: 'convert_rows',
        metadata: {
            hasRows: !!rows,
            rowsType: Array.isArray(rows) ? 'array' : typeof rows,
            rowsLength: Array.isArray(rows) ? rows.length : 'N/A',
            rowsValue: rows,
            firstRow: rows?.[0],
            firstRowKeys: rows?.[0] ? Object.keys(rows[0]) : [],
            sectionTitle,
        },
    })

    // Handle null, undefined, or non-array values
    if (!rows || !Array.isArray(rows)) {
        // If rows is explicitly null (not undefined), this indicates corrupted data
        if (rows === null && !hasAttemptedResync) {
            logger.warn(
                'Rows is null (corrupted data), clearing DataStore and reloading',
                {
                    action: 'convert_rows_null_reload',
                    metadata: {
                        rowsType: typeof rows,
                        rowsValue: rows,
                        sectionTitle,
                    },
                }
            )

            // Mark that we've attempted resync
            hasAttemptedResync = true

            // Clear DataStore and reload page
            triggerDataStoreResync('unknown-form-id')
        } else {
            logger.warn('Rows is not an array, returning empty array', {
                action: 'convert_rows_not_array',
                metadata: {
                    rowsType: typeof rows,
                    rowsValue: rows,
                    sectionTitle,
                },
            })
        }

        return []
    }

    return rows
        .filter((row): row is FormRowDto => row !== null)
        .map((row) => ({
            columns: convertColumns(row.columns),
        }))
}

const convertColumns = (
    columns?: (FieldDto | null)[] | null
): AccFormColumnGroup[] => {
    if (!columns) return []

    // The actual data structure from DynamoDB has nested arrays (column groups),
    // but the TypeScript type is wrong. We need to handle both cases:
    // 1. If columns is already an array of arrays (actual structure)
    // 2. If columns is a flat array (type definition)

    // Check if this is already nested (array of arrays)
    const firstItem = columns[0]
    if (firstItem && Array.isArray(firstItem)) {
        // Already nested - convert each group
        return (columns as unknown as (FieldDto | null)[][]).map(
            (group: (FieldDto | null)[]) => convertFieldGroup(group)
        )
    }

    // Flat array - wrap each field in its own group
    return columns
        .filter((field): field is FieldDto => field !== null)
        .map((field) => [convertField(field)])
}

const convertFieldGroup = (group: (FieldDto | null)[]): AccField[] => {
    return group
        .filter((field): field is FieldDto => field !== null)
        .map((field: FieldDto) => convertField(field))
}

const convertField = (field: FieldDto): AccField => {
    // Debug: Log what we're receiving from DataStore
    logger.debug('Converting field from DataStore:', {
        action: 'convert_field',
        metadata: {
            label: field.label,
            linkId: field.linkId,
            hasLinkId: !!field.linkId,
            hasCodings: !!field.codings,
            codingsCount: field.codings?.length || 0,
            fieldKeys: Object.keys(field),
        },
    })

    return {
        label: field.label || '',
        content: field.content || '',
        linkId: field.linkId || '',
        code: field.code || '',
        system: field.system || '',
        codings: field.codings
            ? field.codings
                  .filter((c): c is NonNullable<typeof c> => c !== null)
                  .map((c) => ({
                      system: c.system || '',
                      code: c.code || '',
                      display: c.display || '',
                  }))
            : undefined,
    }
}
